import React, { useState, useRef } from "react";
import axios from "axios";
import { uploadFileChunked, CHUNKED_THRESHOLD } from "../../utils/chunkedUpload";
import { UploadProgressBar } from "../ui/UploadProgressBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RevisionSubmitBlock = ({ task, showToast, onTaskUpdate, revFiles, setRevFiles }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [submitting, setSubmitting] = React.useState(false);
  const [progressItems, setProgressItems] = useState([]);
  const [overallPercent, setOverallPercent] = useState(0);
  const abortRef = useRef(null);

  const deadline = task.revision_deadline ? new Date(task.revision_deadline).getTime() : null;
  const remaining = deadline ? deadline - Date.now() : null;
  const urgent = remaining !== null && remaining > 0 && remaining < 6 * 60 * 60 * 1000;
  const expired = remaining !== null && remaining <= 0;

  const BLOCKED_EXTENSIONS = [
    '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
    '.sh', '.bash', '.zsh', '.ps1', '.psm1', '.psd1', '.vbs', '.js',
    '.jar', '.class', '.php', '.py', '.rb', '.pl', '.asp', '.aspx',
    '.cgi', '.htaccess', '.svg'
  ];

  const handleSelectFiles = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const blockedFiles = files.filter(f => {
      const ext = '.' + f.name.toLowerCase().split('.').pop();
      return BLOCKED_EXTENSIONS.includes(ext);
    });
    if (blockedFiles.length > 0) {
      showToast(
        `Файлы не разрешены для загрузки: ${blockedFiles.map(f => f.name).join(', ')}. ` +
        'Разрешены: изображения, видео, аудио, PDF, документы Word/Excel.',
        "error"
      );
      e.target.value = '';
      return;
    }

    if (revFiles.length + files.length > 15) {
      showToast("Максимум 15 файлов", "error");
      e.target.value = '';
      return;
    }
    // Сохраняем File-объекты синхронно — кнопка активируется сразу
    setRevFiles(prev => [...prev, ...files.map(f => ({ file: f, name: f.name }))]);
    showToast(`Добавлено файлов: ${files.length}`, "success");
    // Сбрасываем input чтобы можно было выбрать те же файлы повторно
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (revFiles.length === 0) {
      showToast("Добавьте хотя бы один файл с доработкой", "error");
      return;
    }
    setSubmitting(true);
    try {
      // Сжимаем изображения и конвертируем в base64 при отправке
      const compressFile = (item) => new Promise((res) => {
        const file = item.file;
        if (!file.type.startsWith('image/')) {
          // Для не-изображений — прямая конвертация
          const r = new FileReader();
          r.onload = ev => res(ev.target.result);
          r.readAsDataURL(file);
          return;
        }
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          const maxDim = 2048;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
            else { width = Math.round(width * maxDim / height); height = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          res(canvas.toDataURL('image/jpeg', 0.82));
        };
        img.onerror = () => {
          // Если не удалось обработать — отправляем как есть
          const r = new FileReader();
          r.onload = ev => res(ev.target.result);
          r.readAsDataURL(file);
        };
        img.src = url;
      });

      // Split: small files use legacy base64, large go through chunked upload
      const smallItems = revFiles.filter(it => it.file.size < CHUNKED_THRESHOLD);
      const largeItems = revFiles.filter(it => it.file.size >= CHUNKED_THRESHOLD);

      const base64Files = smallItems.length ? await Promise.all(smallItems.map(compressFile)) : [];

      let uploadedRefs = [];
      if (largeItems.length > 0) {
        const controller = new AbortController();
        abortRef.current = controller;
        const totalBytes = largeItems.reduce((s, it) => s + it.file.size, 0) || 1;
        const initial = largeItems.map(it => ({ name: it.name, loaded: 0, total: it.file.size, percent: 0, status: "pending" }));
        setProgressItems(initial);
        setOverallPercent(0);
        let bytesDoneBefore = 0;
        for (let i = 0; i < largeItems.length; i++) {
          const it = largeItems[i];
          setProgressItems(prev => prev.map((p, idx) => idx === i ? { ...p, status: "uploading" } : p));
          try {
            const ref = await uploadFileChunked(it.file, {
              signal: controller.signal,
              onProgress: (p) => {
                setProgressItems(prev => prev.map((pp, idx) => idx === i ? { ...pp, loaded: p.loaded, total: p.total, percent: p.percent } : pp));
                const overallLoaded = bytesDoneBefore + p.loaded;
                setOverallPercent(Math.round((overallLoaded / totalBytes) * 100));
              },
            });
            uploadedRefs.push(ref);
            setProgressItems(prev => prev.map((pp, idx) => idx === i ? { ...pp, status: "done", percent: 100 } : pp));
            bytesDoneBefore += it.file.size;
            setOverallPercent(Math.round((bytesDoneBefore / totalBytes) * 100));
          } catch (err) {
            const aborted = controller.signal.aborted;
            setProgressItems(prev => prev.map((pp, idx) => idx === i ? { ...pp, status: "error", error: aborted ? "Отменено" : (err?.message || "Ошибка") } : pp));
            throw err;
          }
        }
        abortRef.current = null;
      }

      await axios.post(`${API}/api/tasks/${task.id}/submit-revision`, {
        files: base64Files,
        uploaded_refs: uploadedRefs,
        external_links: []
      });
      setProgressItems([]);
      setOverallPercent(0);
      showToast("Доработка отправлена заказчику на подтверждение", "success");
      onTaskUpdate();
    } catch (err) {
      showToast(err?.response?.data?.detail || err?.message || "Ошибка отправки доработки", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelUpload = () => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
  };

  return (
    <>
      <UploadProgressBar
        uploads={progressItems}
        overallPercent={overallPercent}
        onCancel={cancelUpload}
        onClose={() => { setProgressItems([]); setOverallPercent(0); }}
      />
      <h3 className="text-lg font-semibold mb-2" style={{color: '#92400e'}}>Задание отправлено на доработку</h3>
      <p className="text-sm mb-3" style={{color: '#78350f'}}>
        {task.revision_requested_by === 'customer'
          ? 'Заказчик вернул работу на доработку. Загрузите исправленные или недостающие файлы и отправьте задание заказчику на повторное подтверждение.'
          : 'Администратор решил, что работа требует исправлений. Загрузите недостающие файлы и отправьте задание заказчику на повторное подтверждение.'}
      </p>
      {task.customer_revision_comment && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-white border border-yellow-300" data-testid="customer-revision-comment">
          <p className="text-xs font-semibold mb-0.5" style={{color: '#92400e'}}>Комментарий заказчика:</p>
          <p className="text-sm" style={{color: '#78350f'}}>{task.customer_revision_comment}</p>
        </div>
      )}

      {/* Таймер */}
      {remaining !== null && !expired && (
        <div className={`mb-3 px-4 py-3 rounded-lg flex items-center gap-3 ${urgent ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className={`text-xl font-mono font-bold ${urgent ? 'text-red-600' : 'text-yellow-700'}`}>
            {String(Math.floor(remaining / 3600000)).padStart(2,'0')}:{String(Math.floor((remaining % 3600000) / 60000)).padStart(2,'0')}
          </div>
          <div className="text-sm" style={{color: urgent ? '#991b1b' : '#78350f'}}>
            {urgent ? 'Осталось очень мало времени!' : 'Осталось до дедлайна доработки'}
          </div>
        </div>
      )}
      {expired && (
        <div className="mb-3 px-4 py-3 rounded-lg bg-red-50 border border-red-300">
          <p className="text-sm font-semibold text-red-800">Срок доработки истёк, но вы всё ещё можете отправить результат</p>
        </div>
      )}

      {/* Загруженные файлы */}
      {revFiles.length > 0 && (
        <div className="mb-3 space-y-2">
          {revFiles.map((f, idx) => {
            const isImage = f.file.type.startsWith('image/');
            const previewUrl = isImage ? URL.createObjectURL(f.file) : null;
            return (
              <div key={idx} className="flex items-center gap-2 bg-white border border-yellow-200 rounded px-3 py-1.5">
                {isImage && previewUrl && (
                  <img
                    src={previewUrl}
                    alt={f.name}
                    className="w-10 h-10 object-cover rounded flex-shrink-0"
                    onLoad={() => URL.revokeObjectURL(previewUrl)}
                  />
                )}
                <span className="truncate text-gray-700 text-sm flex-1 min-w-0">{f.name}</span>
                <button
                  data-testid={`remove-revision-file-${idx}`}
                  onClick={() => setRevFiles(prev => prev.filter((_, i) => i !== idx))}
                  className="text-red-500 hover:text-red-700 ml-1 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Кнопки */}
      <div className="flex flex-wrap gap-2 mt-2">
        <label
          data-testid="revision-file-input-label"
          className="reversia-btn reversia-btn-md cursor-pointer"
          style={{background: '#d97706', color: '#fff', border: 'none'}}
        >
          {revFiles.length > 0 ? `Добавить ещё (${revFiles.length})` : 'Выбрать файлы'}
          <input
            data-testid="revision-file-input"
            type="file"
            multiple
            className="hidden"
            onChange={handleSelectFiles}
          />
        </label>

        <button
          data-testid="submit-revision-btn"
          onClick={handleSubmit}
          disabled={submitting || revFiles.length === 0}
          className="reversia-btn reversia-btn-md"
          style={{
            background: submitting || revFiles.length === 0 ? '#9ca3af' : '#0d9488',
            color: '#fff',
            border: 'none',
            cursor: submitting || revFiles.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {submitting ? 'Отправка...' : 'Отправить заказчику на подтверждение'}
        </button>
      </div>
    </>
  );
};
